const SAO_PAULO = 'America/Sao_Paulo';

export interface CqvVtmeSnapshotBannerInput {
  generatedAtUtc?: string | null;
  nextRefreshAtUtc?: string | null;
  snapshotStale?: boolean;
  /** Semana, mês ou outro recorte que não lê o snapshot do dia. */
  directQuery?: boolean;
  /** `yyyy-MM-ddTHH:mm` do filtro, horário de São Paulo. */
  periodFromLocal?: string | null;
  periodToLocal?: string | null;
  /** A consulta deste recorte ainda não pintou lista e relatório juntos. */
  periodPending?: boolean;
  /** Pedido explícito de consulta directa (botão ou ação na página). */
  freshNow?: boolean;
  /** Esse pedido ainda está a correr. */
  freshPending?: boolean;
  now?: Date;
}

export interface CqvVtmeSnapshotBanner {
  text: string;
  stale: boolean;
}

export function describeCqvVtmeSnapshotBanner(
  input: CqvVtmeSnapshotBannerInput
): CqvVtmeSnapshotBanner {
  if (input.freshPending) {
    return {
      text: 'A atualizar agora · a consulta directa ainda está a correr',
      stale: true,
    };
  }

  if (input.freshNow) {
    const range = formatPeriodRange(input.periodFromLocal, input.periodToLocal);
    return {
      text: range ? `Dados de agora · ${range}` : 'Dados de agora · consulta directa',
      stale: false,
    };
  }

  if (input.directQuery) {
    const range = formatPeriodRange(input.periodFromLocal, input.periodToLocal);
    if (input.periodPending) {
      return {
        text: range
          ? `Ainda não são os dados de ${range} · a consulta deste período ainda está a correr`
          : 'Ainda não são os dados deste período · a consulta ainda está a correr',
        stale: true,
      };
    }
    return {
      text: range
        ? `Dados de ${range} · consulta directa, fora do snapshot de 15 min`
        : 'Consulta directa do período · fora do snapshot de 15 min',
      stale: false,
    };
  }

  if (!input.generatedAtUtc) {
    return {
      text: 'Dados ainda não disponíveis · a atualização corre a cada 15 min',
      stale: true,
    };
  }

  const now = input.now ?? new Date();
  const generated = new Date(input.generatedAtUtc);
  const generatedHm = formatHm(generated);
  const nextHm = input.nextRefreshAtUtc ? formatHm(new Date(input.nextRefreshAtUtc)) : null;
  const ageMin = Math.max(0, Math.floor((now.getTime() - generated.getTime()) / 60000));
  const stale = Boolean(input.snapshotStale) || ageMin > 20;

  if (stale) {
    const next = nextHm ? ` · próxima atualização ${nextHm}` : '';
    return {
      text: `Dados das ${generatedHm} · parados há ${formatStoppedFor(ageMin)}${next}`,
      stale: true,
    };
  }

  return {
    text: nextHm
      ? `Dados das ${generatedHm} · próxima atualização ${nextHm}`
      : `Dados das ${generatedHm}`,
    stale: false,
  };
}

function formatPeriodRange(fromLocal?: string | null, toLocal?: string | null): string | null {
  const from = formatCivilDate(fromLocal);
  const to = formatCivilDate(toLocal);
  if (!from || !to) return null;
  return `${from} a ${to}`;
}

function formatCivilDate(local?: string | null): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(local ?? '');
  if (!match) return null;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function formatHm(value: Date): string {
  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: SAO_PAULO,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(value);
  const hour = parts.find(part => part.type === 'hour')?.value ?? '00';
  const minute = parts.find(part => part.type === 'minute')?.value ?? '00';
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

function formatStoppedFor(totalMinutes: number): string {
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `${hours} h`;
  return `${hours} h ${minutes} min`;
}
